import { z } from "zod";
import { canonicalizeJson } from "./canonical-json";

export const LUMINAIRE_SPEC_DIGEST_SCOPE = "vaon.luminaire-spec/1:root-without-integrity.contentDigest" as const;

const RESERVED_RECORD_KEYS = new Set(["__proto__", "prototype", "constructor"]);
const idSchema = z.string().trim().min(1).refine(
  (value) => !RESERVED_RECORD_KEYS.has(value),
  "reserved prototype key is not allowed",
);

export const HttpsUrlSchema = z.string().url().refine(
  (value) => new URL(value).protocol === "https:",
  "URL must use HTTPS",
);

export const TypedValueSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("string"), value: z.string() }).strict(),
  z.object({
    type: z.literal("number"),
    value: z.number().finite(),
    unit: z.string().trim().min(1).optional(),
  }).strict(),
  z.object({ type: z.literal("integer"), value: z.number().int() }).strict(),
  z.object({ type: z.literal("boolean"), value: z.boolean() }).strict(),
  z.object({ type: z.literal("url"), value: HttpsUrlSchema }).strict(),
  z.object({ type: z.literal("asset"), artifactId: idSchema }).strict(),
]);

export type TypedValue = z.infer<typeof TypedValueSchema>;

export const LuminaireSelectionSchema = z.record(idSchema, z.array(idSchema));

export type SpecExpression =
  | { op: "selected"; optionId: string }
  | { op: "all" | "any"; args: SpecExpression[] }
  | { op: "not"; arg: SpecExpression };

export const SpecExpressionSchema: z.ZodType<SpecExpression> = z.lazy(() =>
  z.union([
    z.object({ op: z.literal("selected"), optionId: idSchema }).strict(),
    z.object({
      op: z.enum(["all", "any"]),
      args: z.array(SpecExpressionSchema).min(1),
    }).strict(),
    z.object({ op: z.literal("not"), arg: SpecExpressionSchema }).strict(),
  ]),
);

export const PdfRegionSchema = z.object({
  id: idSchema,
  pageIndex: z.number().int().min(1),
  polygon: z.array(z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
  }).strict()).min(3),
  role: z.enum(["group", "option", "rule", "description", "derived-data"]),
}).strict();

export const EvidenceRefSchema = z.object({
  artifactId: idSchema,
  pageIndex: z.number().int().min(1),
  regionIds: z.array(idSchema).min(1),
  extractedText: z.string().optional(),
  confidence: z.number().min(0).max(1).optional(),
  reviewStatus: z.enum(["unreviewed", "reviewed"]),
}).strict();

export const LuminaireOptionSchema = z.object({
  id: idSchema,
  manufacturerCode: z.string(),
  catalogTokens: z.array(z.string().trim().min(1)),
  label: z.string().trim().min(1),
  description: z.string(),
  sortOrder: z.number().int(),
  semanticFacts: z.record(idSchema, TypedValueSchema).optional(),
  evidence: z.array(EvidenceRefSchema).min(1),
}).strict();

export const LuminaireOptionGroupSchema = z.object({
  id: idSchema,
  label: z.string().trim().min(1),
  order: z.number().int(),
  cardinality: z.object({
    min: z.number().int().min(0),
    max: z.number().int().min(0).nullable(),
  }).strict(),
  options: z.array(LuminaireOptionSchema).min(1),
  evidence: z.array(EvidenceRefSchema).min(1),
}).strict().superRefine((group, ctx) => {
  if (group.cardinality.max != null && group.cardinality.max < group.cardinality.min) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["cardinality", "max"],
      message: "max must be greater than or equal to min",
    });
  }
  if (group.cardinality.min > group.options.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["cardinality", "min"],
      message: "min cannot exceed the number of options",
    });
  }
});

export const CatalogGrammarSegmentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("literal"), tokens: z.array(z.string().trim().min(1)).min(1) }).strict(),
  z.object({ kind: z.literal("group"), groupId: idSchema }).strict(),
]);

export const LuminaireConstraintSchema = z.object({
  id: idSchema,
  when: SpecExpressionSchema,
  must: SpecExpressionSchema,
  severity: z.enum(["error", "warning"]),
  message: z.string().trim().min(1),
  evidence: z.array(EvidenceRefSchema).min(1),
}).strict();

export const LuminaireAdvisorySchema = z.object({
  id: idSchema,
  when: SpecExpressionSchema,
  message: z.string().trim().min(1),
  evidence: z.array(EvidenceRefSchema).min(1),
}).strict();

export const LuminaireLookupTableSchema = z.object({
  id: idSchema,
  keyGroupIds: z.array(idSchema).min(1),
  outputs: z.array(idSchema).min(1),
  requiredForFamilyRecipe: z.boolean(),
  rows: z.array(z.object({
    match: z.record(idSchema, z.array(idSchema)),
    values: z.record(idSchema, TypedValueSchema),
    evidence: z.array(EvidenceRefSchema).min(1),
  }).strict()).min(1),
}).strict();

const LuminaireSpecV1BaseSchema = z.object({
  schemaVersion: z.literal("vaon.luminaire-spec/1"),
  productId: idSchema,
  revisionId: idSchema,
  integrity: z.object({
    algorithm: z.literal("sha256"),
    canonicalization: z.literal("RFC8785"),
    digestScope: z.literal(LUMINAIRE_SPEC_DIGEST_SCOPE),
    contentDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  }).strict(),
  product: z.object({
    manufacturerName: z.string().trim().min(1),
    familyCode: z.string().trim().min(1),
    displayName: z.string().trim().min(1),
    sourceUrl: HttpsUrlSchema,
  }).strict(),
  review: z.object({
    status: z.enum(["draft", "reviewed"]),
    optionCoverage: z.enum(["partial", "complete"]),
    ruleCoverage: z.enum(["partial", "complete"]),
    derivedDataCoverage: z.enum(["partial", "complete"]),
    notes: z.array(z.string().trim().min(1)),
  }).strict(),
  sourcePdfArtifactId: idSchema,
  approvedManufacturerFamilyArtifacts: z.array(z.object({
    reviewedArtifactRecordDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    artifactId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  }).strict()),
  catalogGrammar: z.object({
    separator: z.string().min(1),
    segments: z.array(CatalogGrammarSegmentSchema).min(1),
  }).strict(),
  groups: z.array(LuminaireOptionGroupSchema).min(1),
  constraints: z.array(LuminaireConstraintSchema),
  advisories: z.array(LuminaireAdvisorySchema),
  lookupTables: z.array(LuminaireLookupTableSchema),
  regions: z.array(PdfRegionSchema).min(1),
}).strict();

function addDuplicateIssues(
  values: readonly string[],
  path: (string | number)[],
  label: string,
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  values.forEach((value, index) => {
    if (seen.has(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, index],
        message: `duplicate ${label} '${value}'`,
      });
    }
    seen.add(value);
  });
}

function expressionStats(expression: SpecExpression): {
  optionIds: string[];
  nodeCount: number;
  maxDepth: number;
} {
  const optionIds: string[] = [];
  let nodeCount = 0;
  let maxDepth = 0;
  const stack: Array<{ expression: SpecExpression; depth: number }> = [{ expression, depth: 1 }];
  while (stack.length > 0) {
    const current = stack.pop()!;
    nodeCount += 1;
    maxDepth = Math.max(maxDepth, current.depth);
    if (current.expression.op === "selected") {
      optionIds.push(current.expression.optionId);
    } else if (current.expression.op === "not") {
      stack.push({ expression: current.expression.arg, depth: current.depth + 1 });
    } else {
      current.expression.args.forEach((arg) => stack.push({ expression: arg, depth: current.depth + 1 }));
    }
  }
  return { optionIds, nodeCount, maxDepth };
}

function rawSpecExceedsExpressionLimits(input: unknown): boolean {
  if (input == null || typeof input !== "object") return false;
  const raw = input as Record<string, unknown>;
  const roots: unknown[] = [];
  if (Array.isArray(raw.constraints)) {
    raw.constraints.forEach((constraint) => {
      if (constraint == null || typeof constraint !== "object") return;
      const record = constraint as Record<string, unknown>;
      roots.push(record.when, record.must);
    });
  }
  if (Array.isArray(raw.advisories)) {
    raw.advisories.forEach((advisory) => {
      if (advisory == null || typeof advisory !== "object") return;
      roots.push((advisory as Record<string, unknown>).when);
    });
  }

  for (const root of roots) {
    let nodeCount = 0;
    const stack: Array<{ value: unknown; depth: number }> = [{ value: root, depth: 1 }];
    while (stack.length > 0) {
      const current = stack.pop()!;
      nodeCount += 1;
      if (nodeCount > 256 || current.depth > 32) return true;
      if (current.value == null || typeof current.value !== "object") continue;
      const expression = current.value as Record<string, unknown>;
      if (expression.op === "not") {
        stack.push({ value: expression.arg, depth: current.depth + 1 });
      } else if ((expression.op === "all" || expression.op === "any") && Array.isArray(expression.args)) {
        expression.args.forEach((arg) => stack.push({ value: arg, depth: current.depth + 1 }));
      }
    }
  }
  return false;
}

const LuminaireSpecV1ValidatedSchema = LuminaireSpecV1BaseSchema.superRefine((spec, ctx) => {
  const groupIds = spec.groups.map((group) => group.id);
  const groupById = new Map(spec.groups.map((group) => [group.id, group]));
  const regionById = new Map(spec.regions.map((region) => [region.id, region]));
  const optionToGroup = new Map<string, string>();
  const optionRegionOwner = new Map<string, string>();

  addDuplicateIssues(groupIds, ["groups"], "group id", ctx);
  addDuplicateIssues(spec.regions.map((region) => region.id), ["regions"], "region id", ctx);
  addDuplicateIssues(spec.constraints.map((rule) => rule.id), ["constraints"], "constraint id", ctx);
  addDuplicateIssues(spec.advisories.map((advisory) => advisory.id), ["advisories"], "advisory id", ctx);
  addDuplicateIssues(spec.lookupTables.map((table) => table.id), ["lookupTables"], "lookup table id", ctx);
  addDuplicateIssues(
    spec.approvedManufacturerFamilyArtifacts.map((artifact) => artifact.reviewedArtifactRecordDigest),
    ["approvedManufacturerFamilyArtifacts"],
    "reviewed manufacturer-family record digest",
    ctx,
  );

  spec.groups.forEach((group, groupIndex) => {
    addDuplicateIssues(
      group.options.map((option) => option.id),
      ["groups", groupIndex, "options"],
      "option id",
      ctx,
    );
    addDuplicateIssues(
      group.options.map((option) => String(option.sortOrder)),
      ["groups", groupIndex, "options"],
      "option sort order",
      ctx,
    );
    addDuplicateIssues(
      group.options.map((option) => option.catalogTokens.join("\u001F")),
      ["groups", groupIndex, "options"],
      "emitted token sequence",
      ctx,
    );
    group.options.forEach((option, optionIndex) => {
      const renderedCode = option.catalogTokens.join(spec.catalogGrammar.separator);
      if (renderedCode !== option.manufacturerCode) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["groups", groupIndex, "options", optionIndex, "catalogTokens"],
          message: `catalog tokens emit '${renderedCode}', not reviewed code '${option.manufacturerCode}'`,
        });
      }
      const owner = optionToGroup.get(option.id);
      if (owner) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["groups", groupIndex, "options", optionIndex, "id"],
          message: `option id '${option.id}' is already owned by group '${owner}'`,
        });
      } else {
        optionToGroup.set(option.id, group.id);
      }
    });
  });

  const grammarGroupIds = spec.catalogGrammar.segments
    .filter((segment): segment is { kind: "group"; groupId: string } => segment.kind === "group")
    .map((segment) => segment.groupId);
  addDuplicateIssues(grammarGroupIds, ["catalogGrammar", "segments"], "grammar group", ctx);
  grammarGroupIds.forEach((groupId, index) => {
    if (!groupById.has(groupId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["catalogGrammar", "segments", index, "groupId"],
        message: `unknown group '${groupId}'`,
      });
    }
  });
  groupIds.forEach((groupId) => {
    if (!grammarGroupIds.includes(groupId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["catalogGrammar", "segments"],
        message: `group '${groupId}' is missing from the catalog grammar`,
      });
    }
  });

  const checkEvidence = (evidence: z.infer<typeof EvidenceRefSchema>, path: (string | number)[]) => {
    if (spec.review.status === "reviewed" && evidence.reviewStatus !== "reviewed") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, "reviewStatus"],
        message: "a reviewed specification cannot contain unreviewed evidence",
      });
    }
    if (evidence.artifactId !== spec.sourcePdfArtifactId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, "artifactId"],
        message: "evidence artifact must match sourcePdfArtifactId in schema version 1",
      });
    }
    evidence.regionIds.forEach((regionId, regionIndex) => {
      const region = regionById.get(regionId);
      if (!region) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path, "regionIds", regionIndex],
          message: `unknown region '${regionId}'`,
        });
      } else if (region.pageIndex !== evidence.pageIndex) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...path, "pageIndex"],
          message: `evidence page ${evidence.pageIndex} does not match region '${regionId}' page ${region.pageIndex}`,
        });
      }
    });
  };

  spec.groups.forEach((group, groupIndex) => {
    group.evidence.forEach((evidence, index) => checkEvidence(evidence, ["groups", groupIndex, "evidence", index]));
    group.options.forEach((option, optionIndex) => {
      let hasOptionRegion = false;
      option.evidence.forEach((evidence, index) => checkEvidence(
        evidence,
        ["groups", groupIndex, "options", optionIndex, "evidence", index],
      ));
      option.evidence.flatMap((evidence) => evidence.regionIds).forEach((regionId) => {
        const region = regionById.get(regionId);
        if (region?.role !== "option") return;
        hasOptionRegion = true;
        const owner = optionRegionOwner.get(regionId);
        if (owner && owner !== option.id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groups", groupIndex, "options", optionIndex, "evidence"],
            message: `option region '${regionId}' is already owned by option '${owner}'`,
          });
        } else {
          optionRegionOwner.set(regionId, option.id);
        }
      });
      if (!hasOptionRegion) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["groups", groupIndex, "options", optionIndex, "evidence"],
          message: "selectable option must reference at least one option region",
        });
      }
    });
  });

  spec.constraints.forEach((rule, ruleIndex) => {
    const whenStats = expressionStats(rule.when);
    const mustStats = expressionStats(rule.must);
    if (whenStats.nodeCount + mustStats.nodeCount > 256 || Math.max(whenStats.maxDepth, mustStats.maxDepth) > 32) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["constraints", ruleIndex],
        message: "constraint expression exceeds the v1 limit of 256 nodes and depth 32",
      });
    }
    [...whenStats.optionIds, ...mustStats.optionIds].forEach((optionId) => {
      if (!optionToGroup.has(optionId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["constraints", ruleIndex],
          message: `constraint references unknown option '${optionId}'`,
        });
      }
    });
    rule.evidence.forEach((evidence, index) => checkEvidence(evidence, ["constraints", ruleIndex, "evidence", index]));
  });

  spec.advisories.forEach((advisory, advisoryIndex) => {
    const stats = expressionStats(advisory.when);
    if (stats.nodeCount > 256 || stats.maxDepth > 32) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["advisories", advisoryIndex],
        message: "advisory expression exceeds the v1 limit of 256 nodes and depth 32",
      });
    }
    stats.optionIds.forEach((optionId) => {
      if (!optionToGroup.has(optionId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["advisories", advisoryIndex],
          message: `advisory references unknown option '${optionId}'`,
        });
      }
    });
    advisory.evidence.forEach((evidence, index) => checkEvidence(
      evidence,
      ["advisories", advisoryIndex, "evidence", index],
    ));
  });

  spec.lookupTables.forEach((table, tableIndex) => {
    addDuplicateIssues(table.keyGroupIds, ["lookupTables", tableIndex, "keyGroupIds"], "lookup key group", ctx);
    addDuplicateIssues(table.outputs, ["lookupTables", tableIndex, "outputs"], "lookup output", ctx);
    table.keyGroupIds.forEach((groupId, keyIndex) => {
      if (!groupById.has(groupId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lookupTables", tableIndex, "keyGroupIds", keyIndex],
          message: `unknown group '${groupId}'`,
        });
      }
    });
    if (
      table.id === "published-performance"
      && (
        table.keyGroupIds.length !== groupIds.length
        || groupIds.some((groupId) => !table.keyGroupIds.includes(groupId))
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["lookupTables", tableIndex, "keyGroupIds"],
        message: "published-performance must key every option group so family-critical values apply only to an exact configuration",
      });
    }
    const matchKeys = new Set<string>();
    table.rows.forEach((row, rowIndex) => {
      const rowMatchKeys = Object.keys(row.match);
      const valueKeys = Object.keys(row.values);
      if (rowMatchKeys.length !== table.keyGroupIds.length || table.keyGroupIds.some((id) => !rowMatchKeys.includes(id))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lookupTables", tableIndex, "rows", rowIndex, "match"],
          message: "match must contain exactly the lookup table's key groups",
        });
      }
      if (valueKeys.length !== table.outputs.length || table.outputs.some((id) => !valueKeys.includes(id))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lookupTables", tableIndex, "rows", rowIndex, "values"],
          message: "values must contain exactly the lookup table's outputs",
        });
      }
      Object.entries(row.match).forEach(([groupId, optionIds]) => {
        const group = groupById.get(groupId);
        addDuplicateIssues(
          optionIds,
          ["lookupTables", tableIndex, "rows", rowIndex, "match", groupId],
          "matched option",
          ctx,
        );
        if (
          group != null
          && (
            optionIds.length < group.cardinality.min
            || (group.cardinality.max != null && optionIds.length > group.cardinality.max)
          )
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["lookupTables", tableIndex, "rows", rowIndex, "match", groupId],
            message: `lookup match violates '${groupId}' cardinality`,
          });
        }
        optionIds.forEach((optionId) => {
          const owner = optionToGroup.get(optionId);
          if (owner && owner !== groupId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["lookupTables", tableIndex, "rows", rowIndex, "match", groupId],
              message: `option '${optionId}' belongs to group '${owner}', not '${groupId}'`,
            });
          } else if (!owner) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["lookupTables", tableIndex, "rows", rowIndex, "match", groupId],
              message: `unknown option '${optionId}'`,
            });
          }
        });
      });
      const canonicalMatch = table.keyGroupIds.map((groupId) => {
        const selected = new Set(row.match[groupId] ?? []);
        const group = groupById.get(groupId);
        return group?.options
          .filter((option) => selected.has(option.id))
          .sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id))
          .map((option) => option.id)
          .join("\u001E") ?? "";
      }).join("\u001F");
      if (matchKeys.has(canonicalMatch)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["lookupTables", tableIndex, "rows", rowIndex, "match"],
          message: "duplicate lookup match tuple",
        });
      }
      matchKeys.add(canonicalMatch);
      row.evidence.forEach((evidence, index) => checkEvidence(
        evidence,
        ["lookupTables", tableIndex, "rows", rowIndex, "evidence", index],
      ));
    });
  });
});

export const LuminaireSpecV1Schema = z.unknown().superRefine((input, ctx) => {
  if (rawSpecExceedsExpressionLimits(input)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "specification contains an expression exceeding the v1 limit of 256 nodes and depth 32",
    });
  }
}).pipe(LuminaireSpecV1ValidatedSchema);

export type LuminaireSpecV1 = z.infer<typeof LuminaireSpecV1BaseSchema>;

/**
 * Canonical digest payload for an immutable processed revision. The digest
 * value itself is excluded to avoid a circular hash; its algorithm and
 * canonicalization declaration remain covered by the digest.
 */
export function canonicalizeLuminaireSpecContent(spec: LuminaireSpecV1): string {
  return canonicalizeJson({
    ...spec,
    integrity: {
      algorithm: spec.integrity.algorithm,
      canonicalization: spec.integrity.canonicalization,
      digestScope: spec.integrity.digestScope,
    },
  });
}

export async function computeLuminaireSpecContentDigest(
  spec: LuminaireSpecV1,
): Promise<string> {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.subtle == null) {
    throw new Error("Web Crypto SHA-256 is required to verify a processed luminaire revision");
  }
  const content = new TextEncoder().encode(canonicalizeLuminaireSpecContent(spec));
  const digest = await cryptoApi.subtle.digest("SHA-256", content);
  const hexadecimal = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `sha256:${hexadecimal}`;
}

export type LuminaireSelection = z.infer<typeof LuminaireSelectionSchema>;
export type EvidenceRef = z.infer<typeof EvidenceRefSchema>;
export type LuminaireOptionGroup = z.infer<typeof LuminaireOptionGroupSchema>;
export type LuminaireOption = z.infer<typeof LuminaireOptionSchema>;
